import { useState, useEffect, useCallback } from 'react'
import type { GameItem } from '../../i18n'
import {
  buildPuzzleRounds,
  buildPairsCards,
  type PuzzleDifficulty,
  type PuzzleMode,
  type PuzzleRound,
  type PairsCard,
} from './puzzleUtils'

export type { PuzzleDifficulty, PuzzleMode }

interface PuzzleState {
  // puzzle mode
  rounds: PuzzleRound[]
  currentRoundIdx: number
  correctFlashId: string | null
  wrongId: string | null
  // pairs mode
  cards: PairsCard[]
  selectedIds: string[]
  matchedImageIds: Set<number>
  wrongPairIds: string[] | null
  // shared
  moves: number
  startTime: number | null
  endTime: number | null
  gameComplete: boolean
}

function initState(
  difficulty: PuzzleDifficulty,
  mode: PuzzleMode,
  images: GameItem[],
): PuzzleState {
  return {
    rounds:          mode === 'puzzle' ? buildPuzzleRounds(images, difficulty) : [],
    currentRoundIdx: 0,
    correctFlashId:  null,
    wrongId:         null,
    cards:           mode === 'pairs' ? buildPairsCards(images, difficulty) : [],
    selectedIds:     [],
    matchedImageIds: new Set(),
    wrongPairIds:    null,
    moves:           0,
    startTime:       null,
    endTime:         null,
    gameComplete:    false,
  }
}

export function usePuzzle(
  difficulty: PuzzleDifficulty,
  mode: PuzzleMode,
  images: GameItem[],
) {
  const [state, setState] = useState<PuzzleState>(() =>
    initState(difficulty, mode, images),
  )

  const resetGame = useCallback(() => {
    setState(initState(difficulty, mode, images))
  }, [difficulty, mode, images])

  // Re-init when images or mode/difficulty change (e.g. theme loaded async)
  useEffect(() => {
    setState(initState(difficulty, mode, images))
  }, [difficulty, mode, images])

  // Puzzle mode: advance to next round after correct-flash
  useEffect(() => {
    if (state.correctFlashId === null) return
    const timer = setTimeout(() => {
      setState(prev => {
        if (prev.correctFlashId === null) return prev
        const nextIdx = prev.currentRoundIdx + 1
        const gameComplete = nextIdx >= prev.rounds.length
        return {
          ...prev,
          currentRoundIdx: nextIdx,
          correctFlashId:  null,
          gameComplete,
          endTime: gameComplete ? Date.now() : prev.endTime,
        }
      })
    }, 700)
    return () => clearTimeout(timer)
  }, [state.correctFlashId])

  // Puzzle mode: clear wrong indicator
  useEffect(() => {
    if (state.wrongId === null) return
    const timer = setTimeout(() => {
      setState(prev => ({ ...prev, wrongId: null }))
    }, 650)
    return () => clearTimeout(timer)
  }, [state.wrongId])

  // Pairs mode: clear wrong pair indicator
  useEffect(() => {
    if (state.wrongPairIds === null) return
    const timer = setTimeout(() => {
      setState(prev => ({ ...prev, wrongPairIds: null }))
    }, 650)
    return () => clearTimeout(timer)
  }, [state.wrongPairIds])

  const selectAlternative = useCallback((altId: string) => {
    setState(prev => {
      if (prev.correctFlashId !== null || prev.gameComplete) return prev
      const round = prev.rounds[prev.currentRoundIdx]
      if (!round) return prev

      const alt = round.alternatives.find(a => a.id === altId)
      if (!alt) return prev

      const startTime = prev.startTime ?? Date.now()
      const moves = prev.moves + 1

      if (alt.isCorrect) {
        return { ...prev, startTime, moves, correctFlashId: altId }
      }
      return { ...prev, startTime, moves, wrongId: altId }
    })
  }, [])

  const selectCard = useCallback((cardId: string) => {
    setState(prev => {
      if (prev.gameComplete || prev.wrongPairIds !== null) return prev

      const card = prev.cards.find(c => c.id === cardId)
      if (!card) return prev
      if (prev.matchedImageIds.has(card.imageId)) return prev
      if (prev.selectedIds.includes(cardId)) return prev

      const startTime = prev.startTime ?? Date.now()

      if (prev.selectedIds.length === 0) {
        return { ...prev, startTime, selectedIds: [cardId] }
      }

      const firstId = prev.selectedIds[0]
      const firstCard = prev.cards.find(c => c.id === firstId)
      if (!firstCard) return prev

      const moves = prev.moves + 1

      if (firstCard.imageId === card.imageId) {
        // Correct pair
        const matchedImageIds = new Set(prev.matchedImageIds)
        matchedImageIds.add(card.imageId)
        const totalPairs = prev.cards.length / 2
        const gameComplete = matchedImageIds.size >= totalPairs
        return {
          ...prev,
          moves,
          matchedImageIds,
          selectedIds: [],
          wrongPairIds: null,
          gameComplete,
          endTime: gameComplete ? Date.now() : prev.endTime,
        }
      }

      // Wrong pair
      return {
        ...prev,
        moves,
        selectedIds: [],
        wrongPairIds: [firstId, cardId],
      }
    })
  }, [])

  return { state, resetGame, selectAlternative, selectCard }
}
